import { AxiosError, AxiosResponse } from 'axios';
import {
  ApiException,
  BadRequestException,
  UnauthorizedException,
  ForbiddenException,
  NotFoundException,
  ServerErrorException,
  UnknownApiException,
  NetworkException,
} from './apiException';

export function parseApiSuccessResponse(response: AxiosResponse): any {
  if (response.status == 200 || response.status == 201) {
    return response.data;
  }
}

export function parseErrorString(data: any): string {
  let errorData: string;
  if (typeof data === 'object' && data !== null) {
    errorData = JSON.stringify(data, null, 2);
  } else {
    errorData = String(data);
  }

  return errorData;
}

export function parseApiErrorResponse(error: AxiosError): ApiException {
  if (!error.response) {
    throw new NetworkException();
  }

  const { status, data } = error.response;

  let errorData: string;
  if (typeof data === 'object' && data !== null) {
    errorData = JSON.stringify(data, null, 2);
  } else {
    errorData = String(data);
  }

  switch (status) {
    case 400:
      throw new BadRequestException(errorData, status);
    case 401:
      throw new UnauthorizedException(errorData, status);
    case 403:
      throw new ForbiddenException(errorData, status);
    case 404:
      throw new NotFoundException(errorData, status);
    case 500:
      throw new ServerErrorException(errorData, status);
    default:
      throw new UnknownApiException(errorData, status);
  }
}

export function buildUrl(
  protocol: string,
  host: string,
  path: string,
  queryParams?: Record<string, any>
): string {
  const url = new URL(`${protocol}://${host}${path}`);

  if (queryParams) {
    Object.keys(queryParams).forEach((key) => {
      if (queryParams[key] !== undefined) {
        url.searchParams.append(key, String(queryParams[key]));
      }
    });
  }

  return url.toString();
}

export function parseValidationErrors(
  responseBody: string
): Record<string, string> {
  const json = JSON.parse(responseBody) as Record<string, any>;
  const errorCode = json['error_code'] as string;

  if (errorCode === 'validation_error') {
    const errors = json['errors'] as Record<string, any>;
    return Object.fromEntries(
      Object.entries(errors).map(([key, value]) => {
        const val = (value as Array<any>)[0] as string;
        return [key, val];
      })
    );
  }

  return {};
}

export function parseErrorsByCode(
  responseBody: string,
  errorCode: string
): Record<string, any> {
  const json = JSON.parse(responseBody) as Record<string, any>;
  const errorKey = json['error_code'] as string;

  if (errorKey === errorCode) {
    const errors = json['errors'] as Record<string, any>;
    return errors;
  }

  return {};
}

export function parseErrorMessageByCode(
  responseBody: string,
  errorCode: string
): string {
  const json = JSON.parse(responseBody) as Record<string, any>;
  const errorKey = json['error_code'] as string;

  if (errorKey === errorCode) {
    const error = json['message'] as string;
    return error;
  }

  return '';
}

export function isErrorCodeExists(
  responseBody: string,
  errorCode: string
): boolean {
  const json = JSON.parse(responseBody) as Record<string, any>;
  const errorKey = json['error_code'] as string | undefined;
  return errorKey === errorCode;
}

type ParsedErrors = {
  errorCode?: string;
  errors?: Record<string, any>;
};

export function parseErrorsWithErrorCode(responseBody: string): ParsedErrors {
  let errorCode: string | undefined;
  let errors: Record<string, any> | undefined;

  try {
    const json = JSON.parse(responseBody) as Record<string, any>;
    errorCode = json['error_code'] as string | undefined;
    errors = json['errors'] as Record<string, any> | undefined;
  } catch (e) {}

  if (errorCode && errorCode.trim() === '') {
    errorCode = undefined;
  }
  if (errors && Object.keys(errors).length === 0) {
    errors = undefined;
  }

  return {
    errorCode,
    errors,
  };
}

export function parseErrorCode(responseBody: string): string | null {
  let errorCode: string | null = null;
  try {
    const json = JSON.parse(responseBody) as Record<string, any>;
    errorCode = (json['error_code'] as string | undefined) || null;
  } catch (e) {}

  if (errorCode !== null && errorCode.length === 0) {
    errorCode = null;
  }

  return errorCode;
}
